import { Component, Input, SimpleChanges, OnChanges, OnDestroy, ViewEncapsulation, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { finalize, forkJoin, Observable, Subject, Subscription, take, takeUntil } from 'rxjs';
import L, { CRS } from 'leaflet';
import 'leaflet-wms-header';
import 'leaflet-area-select';
import { default as vectorTileLayer } from 'leaflet-vector-tile-layer';

import '@geoman-io/leaflet-geoman-free';
import 'leaflet.polylinemeasure';
import 'leaflet.markercluster';
import 'Leaflet.Deflate';
import { SearchControl } from 'leaflet-geosearch';

import dayjs from 'dayjs';

import { AppConfig } from 'src/app/app.config';
import { MapService, TaskSubLocation, WorksiteSubLocation } from './map.service';
import { MapLayerService } from './map-layer.service';
import { HttpClient } from '@angular/common/http';
import { BlahService } from '@headpower/blah-ng';
import { SessionStorage } from 'src/app/shared/functions/SessionStorage';
import { FeatureFlagService } from '@headpower/featureflags';
import { MatTableDataSource } from '@angular/material/table';
import { TaskNetworkComponentService } from 'src/app/services/task-network-component.service';
import { TaskNetworkComponentConfig } from 'src/app/tasks/task/models/network-component.model';
import { ToasterService } from '@headpower/layout';
import HpoAddressSearchProvider from './hpo-address-search-provider';
import HpoNetworkComponentSearchProvider from './hpo-network-component-search-provider';
import { HybridUiMapService } from 'src/app/hybrid-ui-map.service';
import { UserInterfaceService } from 'src/app/services/user-interface.service';

export class MapPinItem {
  Id: string;
  EntityType: 'task' | 'worksite';
  Lat: number;
  Lon: number;
  Color: 'green' | 'red' | 'white' | 'gray' | 'blue' = 'white';
  Title: string = '';
  RouterLink: string = '';

  Properties: any = {}

  _marker: L.Marker;
}

export class MapNetWorkComponentInfo {
  Id: number;
  Lat: number;
  Lon: number;
  ComponentType: string;
  Label: string;

  Selected: boolean = false;
}

export class CustomerLayer {
  Id: string;
  Name: string;
  Metadata: boolean;

  Layer: L.Layer;
}

export interface IHighlightEntity {
  entityType: 'task' | 'worksite';
  entityId: string;
}

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: [
    './map.component.scss',
    '../../../../node_modules/leaflet-geosearch/dist/geosearch.css',
    '../../../../node_modules/leaflet.markercluster/dist/MarkerCluster.css',
    '../../../../node_modules/leaflet.markercluster/dist/MarkerCluster.Default.css',
    '../../../../node_modules/leaflet.polylinemeasure/Leaflet.PolylineMeasure.css'
  ],
  encapsulation: ViewEncapsulation.None
})
export class MapComponent implements AfterViewInit, OnChanges, OnDestroy {
  private map: L.Map;
  private infoControlButton: HTMLDivElement;
  private selectControlButton: HTMLDivElement;
  private bboxControlButton: HTMLDivElement;


  private markerClusterGroup: L.MarkerClusterGroup;
  private markerGroup: L.FeatureGroup;
  private observations: L.GeoJSON;
  private subLocations: L.GeoJSON;
  private lockedSubLocations: L.GeoJSON;

  private mapInitialized: boolean = false;

  private mapBounds: L.LatLngBounds = null;
  public bboxFilterActive: boolean = false;

  public infoCoords: L.LatLng = null;
  public infoActive: boolean = false;
  public infoVisible: boolean = false;
  public selectedObservation: any = null;
  public creatingTaskFromObservation: boolean = false;
  public infoItems: MatTableDataSource<MapNetWorkComponentInfo> = new MatTableDataSource();
  public infoColumns = ["ComponentType", "Label", "CreateTask"]; // , "Id"
  public areaSelectedColumns = ["Selected", "ComponentType", "Label"]; // , "Id"
  private metadataLayerAvailable = false;

  public attachedTask: any;
  public mobile: boolean;

  // Toggles smooth transitions on map.
  // Looks cool but causes a lot of map tile requests and
  // requires more from device performancewise.
  private smoothTransitions = true;

  private accessToken: string;

  private resizeSubscription: Subscription;
  private bboxSubscription: Subscription;

  private customerLayers: CustomerLayer[] = [];


  private readonly defaultPositionAndZoom: L.MapOptions = {
    center: [64.180708, 25.803222],
    zoom: 6,
  }

  private highlightedItem: MapPinItem | null = null;

  private destroy$: Subject<void> = new Subject();

  private ff_IwmPOC: boolean = false; // Feature flag for IWM POC

  @SessionStorage
  private selectedBaseLayerName: string;

  @Input() leafletId: string;
  @Input() baseMapName: string = '';
  @Input() items: MapPinItem[] = [];
  @Input() highlightItem: IHighlightEntity = undefined;
  @Input() resizeObservable: Observable<void>;
  @Input() viewOnly: boolean = false; // can not be changed after init
  @Input() useClusterMarkers: boolean = false; // can not be changed after init
  @Input() satnet: boolean = false; // can not be changed after init
  @Input() maxAutoZoom: number = null;

  constructor(
    private featureFlagService: FeatureFlagService,
    private oAuthService: OAuthService,
    private mapService: MapService,
    private mapLayerService: MapLayerService,
    private uiMapService: HybridUiMapService,
    private taskNetworkComponentService: TaskNetworkComponentService,
    private userInterfaceService: UserInterfaceService,
    private toaster: ToasterService,
    private config: AppConfig,
    private router: Router,
    private blahService: BlahService,
    private http: HttpClient,
  ) {

    this.featureFlagService.isFeatureAvailable('IWM.ListTaskPOC').subscribe((isAvailable) => {
      this.ff_IwmPOC = isAvailable;
    });

    this.accessToken = this.oAuthService.getAccessToken();

    // Observe device screen size and change the layout accordingly
    this.userInterfaceService.mobile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.mobile = state;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.items !== undefined) {
      this.handleMapMarkers();
    };

    if (changes.highlightItem !== undefined && this.highlightItem !== undefined) {
      this.handleHighlightedItem();
    }
  }


  public ngAfterViewInit(): void {
    this.initMap().then(() => {
      this.map.invalidateSize();
      this.handleMapMarkers();
      this.handleHighlightedItem();
    });

  }

  private handleMapMarkers() {
    if (this.mapInitialized) {
      this.updateMapMarkers();
    }
  }

  private handleHighlightedItem() {
    if (this.mapInitialized && this.highlightItem !== undefined) {
      this.highlightedItem = null;

      // check if task alredy exists in items
      let mapItem = this.getAllItems().find(o => o.Id === this.highlightItem.entityId.toLowerCase());
      if (mapItem === undefined || mapItem._marker === undefined) {
        // fetch map pin
        this.getHighlightedMapItemDetails();
      } else {
        // proceed to highlighting item
        this.highlightMarker();
      }
    }
  }

  public ngOnDestroy(): void {
    // clear item marker references
    this.items.forEach(o => {
      o._marker = undefined;
    })

    this.destroy$.next();
    this.destroy$.complete();

    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
    }

    if (this.bboxSubscription) {
      this.bboxSubscription.unsubscribe();
    }

    if (this.map) {
      this.map.remove();
      this.map = undefined;
    }
  }

  private async initMap(): Promise<void> {
    if (this.leafletId === undefined || this.leafletId === null) {
      console.error("MapComponent must be given an id.")
    }

    const self = this;

    // disable edit by default
    L.PM.setOptIn(true);

    // Create map
    this.map = L.map(
      this.leafletId,
      {
        ...this.defaultPositionAndZoom,
        pmIgnore: false,
        worldCopyJump: true,
        maxZoom: 19
      }

    );

    const thisComponent = this;
    this.map.on('baselayerchange', function (o) {
      thisComponent.selectedBaseLayerName = o.name;
    })

    // bind to parent resize observable
    if (this.resizeObservable) {
      this.resizeSubscription = this.resizeObservable.subscribe(() => {
        this.map.invalidateSize();
      });
    }

    // add marker group
    if (this.useClusterMarkers) {
      this.markerClusterGroup = L.markerClusterGroup();
      this.map.addLayer(this.markerClusterGroup);

    } else {
      this.markerGroup = L.featureGroup();
      this.map.addLayer(this.markerGroup);
    }

    // add geoJson (sublocations) layer
    this.subLocations = L.geoJSON(
      [], // empty layer
      {
        pmIgnore: false, // enable editing
        style: {
          color: '#0083a9' // editable sublocations - headpower blue
        },
        pointToLayer: function (geoJsonPoint, latlng) {
          return L.circleMarker(latlng, {
            color: '#0083a9', // editable sublocations - headpower blue
            radius: 10
          });
        }
      });
    this.subLocations.addTo(this.map);
    this.lockedSubLocations = L.geoJSON(
      [], // empty layer
      {
        pmIgnore: true, // disable editing
        style: {
          color: '#dc911b' // locked sublocations - headpower orange
        },
        pointToLayer: function (geoJsonPoint, latlng) {
          return L.circleMarker(latlng, {
            color: '#dc911b', // locked sublocations - headpower orange
            radius: 10
          });
        }
      });
    this.lockedSubLocations.addTo(this.map);

    this.observations = L.geoJSON(
      [], // empty layer
      {
        filter: function (f) {
          return f.geometry.type == "Polygon";
        },
        pmIgnore: true, // disable editing
        style: function (f) {
          return {
            color: component.getSatnetFeatureColor(f)
          }
        }
      });

    // add scale control
    L.control.scale({
      imperial: false,
      position: 'bottomright',
    }).addTo(this.map);

    // add measure control
    if (!this.viewOnly) {
      L.control.polylineMeasure(<any>{
        position: 'topleft',            // Position to show the control. Values: 'topright', 'topleft', 'bottomright', 'bottomleft'
        unit: 'metres',                 // Show imperial or metric distances. Values: 'metres', 'landmiles', 'nauticalmiles'
        clearMeasurementsOnStop: true,  // Clear all the measurements when the control is unselected
        showBearings: false,            // Whether bearings are displayed within the tooltips
        bearingTextIn: 'In',             // language dependend label for inbound bearings
        bearingTextOut: 'Out',          // language dependend label for outbound bearings
        tooltipTextFinish: 'Click to <b>finish line</b><br>',
        tooltipTextDelete: 'Press SHIFT-key and click to <b>delete point</b>',
        tooltipTextMove: 'Click and drag to <b>move point</b><br>',
        tooltipTextResume: '<br>Press CTRL-key and click to <b>resume line</b>',
        tooltipTextAdd: 'Press CTRL-key and click to <b>add point</b>',
        // language dependend labels for point's tooltips
        measureControlTitleOn: 'Turn on PolylineMeasure',   // Title for the control going to be switched on
        measureControlTitleOff: 'Turn off PolylineMeasure', // Title for the control going to be switched off
        measureControlLabel: '&#8614;', // Label of the Measure control (maybe a unicode symbol)
        measureControlClasses: [],      // Classes to apply to the Measure control
        showClearControl: false,        // Show a control to clear all the measurements
        clearControlTitle: 'Clear Measurements', // Title text to show on the clear measurements control button
        clearControlLabel: '&times',    // Label of the Clear control (maybe a unicode symbol)
        clearControlClasses: [],        // Classes to apply to clear control button
        showUnitControl: false,         // Show a control to change the units of measurements
        distanceShowSameUnit: false,    // Keep same unit in tooltips in case of distance less then 1 km/mi/nm
        unitControlTitle: {             // Title texts to show on the Unit Control button
          text: 'Change Units',
          metres: 'metres',
          landmiles: 'land miles',
          nauticalmiles: 'nautical miles'
        },
        unitControlLabel: {             // Unit symbols to show in the Unit Control button and measurement labels
          metres: 'm',
          kilometres: 'km',
          feet: 'ft',
          landmiles: 'mi',
          nauticalmiles: 'nm'
        },
        tempLine: {                     // Styling settings for the temporary dashed line
          color: '#00f',              // Dashed line color
          weight: 2                   // Dashed line weight
        },
        fixedLine: {                    // Styling for the solid line
          color: '#006',              // Solid line color
          weight: 2                   // Solid line weight
        },
        startCircle: {                  // Style settings for circle marker indicating the starting point of the polyline
          color: '#000',              // Color of the border of the circle
          weight: 1,                  // Weight of the circle
          fillColor: '#0f0',          // Fill color of the circle
          fillOpacity: 1,             // Fill opacity of the circle
          radius: 3                   // Radius of the circle
        },
        intermedCircle: {               // Style settings for all circle markers between startCircle and endCircle
          color: '#000',              // Color of the border of the circle
          weight: 1,                  // Weight of the circle
          fillColor: '#ff0',          // Fill color of the circle
          fillOpacity: 1,             // Fill opacity of the circle
          radius: 3                   // Radius of the circle
        },
        currentCircle: {                // Style settings for circle marker indicating the latest point of the polyline during drawing a line
          color: '#000',              // Color of the border of the circle
          weight: 1,                  // Weight of the circle
          fillColor: '#f0f',          // Fill color of the circle
          fillOpacity: 1,             // Fill opacity of the circle
          radius: 3                   // Radius of the circle
        },
        endCircle: {                    // Style settings for circle marker indicating the last point of the polyline
          color: '#000',              // Color of the border of the circle
          weight: 1,                  // Weight of the circle
          fillColor: '#f00',          // Fill color of the circle
          fillOpacity: 1,             // Fill opacity of the circle
          radius: 3                   // Radius of the circle
        },
      }).addTo(this.map);
    }

    // Add info button control
    if (!this.viewOnly) {
      this.infoControlButton = L.DomUtil.create('div',
        'leaflet-control leaflet-bar leaflet-select-control',
        (this.map as any)._controlCorners.topleft
      );
      this.infoControlButton.innerHTML = '<div title="Info" class="icon" style="background-image: url(\'./assets/mapmarkers/control-info.png\')"></div>';

      L.DomEvent.on(this.infoControlButton, 'click', function (e) {
        L.DomEvent.stopPropagation(e);
        component.toggleInfoActive(component);
      });
    }

    // add draw controls but hide them initially
    this.map.pm.addControls({
      position: 'topleft',

      drawMarker: false,
      drawCircleMarker: false,
      drawRectangle: false,
      drawCircle: false,
      drawText: false,

      cutPolygon: false,
      rotateMode: false,
    });
    this.setEditControls(false);

    let allLayers = this.mapLayerService.getAllBackgroundLayers();
    if (this.baseMapName && allLayers[this.baseMapName] !== undefined) {
      allLayers[this.baseMapName].addTo(this.map);
    } else {
      let bgLayers = await this.mapLayerService.getVisibleBackgroundLayers();

      if (this.selectedBaseLayerName && bgLayers[this.selectedBaseLayerName] !== undefined) {
        // use previously selected layer
        bgLayers[this.selectedBaseLayerName].addTo(this.map);
      } else {
        // Get default layer 
        let defaultLayer = await this.mapLayerService.getDefaultBackgroundLayerName();
        bgLayers[defaultLayer].addTo(this.map);
      }

      let overlays = await this.mapLayerService.getVisibleOverlayLayers();

      var sanitazeString = (s: string) => {
        let result = s.split('\\r\\n').join(' ');
        result = result.split('\\r').join(' ');
        result = result.split('\\n').join(' ');

        return result;
      }

      // add layer controls
      const layerControls = L.control.layers(bgLayers, overlays);
      layerControls.addTo(this.map);

      // fetch customer layers and add them to overlay layers
      this.mapService.getCustomerLayers().subscribe((res) => {

        this.customerLayers = [];

        res.features.forEach(feature => {
          const name = `${feature.properties.CompanyName} - ${feature.properties.Name}`

          let customerLayer = new CustomerLayer();
          customerLayer.Id = feature.properties.Id;
          customerLayer.Name = name;
          customerLayer.Metadata = feature.properties.HasMetadata;
          this.customerLayers.push(customerLayer);

          let bounds = undefined;
          if (feature.geometry !== null) {
            bounds = new L.LatLngBounds(
              L.latLng(feature.geometry.bbox[1], feature.geometry.bbox[0]),
              L.latLng(feature.geometry.bbox[3], feature.geometry.bbox[2])
            );
          }

          let component = this;
          if (feature.properties.HasMetadata) {
            let url = `${this.config.backendMapUri}CommonMapWebAPI/api/Wmts/GetCustomerLayerTile`
              + `?layerId=${feature.properties.Id}`
              + '&tileMatrix=EPSG:3857:{z}'
              + '&tileMatrixSet=EPSG:3857'
              + '&tileCol={x}'
              + '&tileRow={y}'
              + '&format=application/vnd.mapbox-vector-tile';

            const tileLayer = vectorTileLayer(
              url,
              {
                crs: CRS.EPSG3857,
                bounds: bounds,
                minZoom: 8,
                maxZoom: 19,
                fetchOptions: {
                  headers: {
                    Authorization: 'Bearer ' + this.accessToken,
                    'ngsw-bypass': ''
                  }
                },
                style: component.getFeatureStyle,
                filter: component.filterFeature
              });

            layerControls.addOverlay(tileLayer, name);
            customerLayer.Layer = tileLayer;
            this.metadataLayerAvailable = true;

          } else {
            const layer = L.TileLayer.wmsHeader(
              `${this.config.backendMapUri}CommonMapWebAPI/api/Wms/GetCustomerMapLayer?`,
              <L.WMSOptions>{
                layers: '',
                layerId: feature.properties.Id,
                format: 'image/png',
                transparent: true,
                crs: CRS.EPSG3857,
                bounds: bounds,
                minZoom: 8,
                maxZoom: 19,
              },
              [
                { header: 'Authorization', value: 'Bearer ' + this.accessToken },
                { header: 'ngsw-bypass', value: '' }
              ],
              null,
              null);


            layerControls.addOverlay(layer, name);
            customerLayer.Layer = layer;
          }
        })

        if (this.metadataLayerAvailable) {
          const componentSearchControl = SearchControl({
            searchLabel: this.blahService.blah('map.search.componentLabel'),
            clearSearchLabel: this.blahService.blah('map.search.clear'),
            notFoundMessage: this.blahService.blah('map.search.componentNotFound'),
            showMarker: false,
            style: 'button',
            zoomLevel: 17,
            resultFormat: ({ result }) => `${sanitazeString(result.raw.properties["Label"])} (${this.blahService.blah('networkComponents.componentTypes.' + result.raw.properties["ComponentType"])})`,
            autoClose: true,
            maxSuggestions: 20,
            provider: new HpoNetworkComponentSearchProvider(this.config, this.oAuthService),
          });

          this.map.addControl(componentSearchControl);
        }

      });

      // add search controls
      const addressSearchControl = SearchControl({
        searchLabel: this.blahService.blah('map.search.addressLabel'),
        clearSearchLabel: this.blahService.blah('map.search.clear'),
        notFoundMessage: this.blahService.blah('map.search.addressNotFound'),
        showMarker: false,
        style: 'bar',
        zoomLevel: 17,
        maxSuggestions: 10,
        provider: new HpoAddressSearchProvider(this.config, this.oAuthService),
      });

      this.map.addControl(addressSearchControl);


      this.map.on('geosearch/showlocation', function (o) {
        // if result is component and has layer information, toggle layer as visible
        let result = (<any>o).location;

        if (result && result.layerId) {
          let layer = component.customerLayers.find(o => o.Id == result.layerId);
          if (!component.map.hasLayer(layer.Layer)) {
            component.map.addLayer(layer.Layer)
          }
        }
      });

    }

    // add new drawn entities to existing layer group
    this.map.pm.setGlobalOptions({
      layerGroup: this.subLocations
    })

    let lang = this.blahService.currentLocale.substring(0, 2);
    switch (lang) {
      case 'fi':
        this.map.pm.setLang('fi');
        break;

      case 'sv':
        this.map.pm.setLang('sv');
        break;

      case 'en':
      default:
        this.map.pm.setLang('en');
        break;
    }

    // begin listening draw changes
    this.subLocations.on('pm:edit', (e) => {
      // save sublocation
      if (this.highlightItem.entityType == 'task') {

        let subLocation = new TaskSubLocation();
        subLocation.Id = (<any>e.layer).feature.properties.id;
        subLocation.TaskId = (<any>e.layer).feature.properties.taskId;
        subLocation.Location = (<any>e.layer).toGeoJSON().geometry;
        subLocation.SystemGenerated = false;

        this.mapService.updateTaskSubLocation(subLocation).subscribe();

      } else if (this.highlightItem.entityType == 'worksite') {

        let subLocation = new WorksiteSubLocation();
        subLocation.Id = (<any>e.layer).feature.properties.id;
        subLocation.WorksiteId = (<any>e.layer).feature.properties.worksiteId;
        subLocation.Location = (<any>e.layer).toGeoJSON().geometry;

        this.mapService.updateWorksiteSubLocation(subLocation).subscribe();
      }
    });

    this.map.on('pm:create', (e) => {
      // save sublocation
      if (this.highlightItem.entityType == 'task') {

        let subLocation = new TaskSubLocation();
        subLocation.Id = '00000000-0000-0000-0000-000000000000';
        subLocation.TaskId = this.highlightItem.entityId;
        subLocation.Location = (<any>e.layer).toGeoJSON().geometry;

        this.mapService.addTaskSubLocation(subLocation).subscribe((res) => {
          let feature = this.makeFeatureFromSubLocation(res);
          // Remove created layer and add the one returned from BE.
          // this makes newly created layers as features too and allows editing them
          this.subLocations.removeLayer(e.layer);
          this.addSubLocationToLayer(feature);
        });

      } else if (this.highlightItem.entityType == 'worksite') {

        let subLocation = new WorksiteSubLocation();
        subLocation.Id = '00000000-0000-0000-0000-000000000000';
        subLocation.WorksiteId = this.highlightItem.entityId;
        subLocation.Location = (<any>e.layer).toGeoJSON().geometry;

        this.mapService.addWorksiteSubLocation(subLocation).subscribe((res) => {
          let feature = this.makeFeatureFromSubLocation(res);
          // Remove created layer and add the one returned from BE.
          // this makes newly created layers as features too and allows editing them
          this.subLocations.removeLayer(e.layer);
          this.addSubLocationToLayer(feature);
        });
      }

    });

    this.map.on('pm:remove', (e) => {
      // remove sublocation
      let subLocationId = (<any>e.layer).feature.properties.id;
      if (this.highlightItem.entityType == 'task') {
        this.mapService.removeTaskSubLocation(subLocationId).subscribe();
      } else if (this.highlightItem.entityType == 'worksite') {
        this.mapService.removeWorksiteSubLocation(subLocationId).subscribe();
      }
    });

    let component = this;
    this.map.on('click', function (e) {

      if (component.infoActive) {
        component.closeInfo();

        component.infoCoords = e.latlng;
        let newData: MapNetWorkComponentInfo[] = []
        component.mapService
          .getFeaturesWithMetadataInLocation(component.infoCoords.lat, component.infoCoords.lng)
          .subscribe((res) => {
            res.features.forEach(f => {

              let infoItem = new MapNetWorkComponentInfo();
              infoItem.Id = f.properties['Id'];
              infoItem.ComponentType = f.properties['ComponentType'];
              let label = f.properties['ext_map_label_'] ?? f.properties['ext_identifica'] ?? '';
              infoItem.Label = label.split('\\r\\n').filter(str => str.length > 0).join(' ').split('\\n').filter(str => str.length > 0).join(' ')

              infoItem.Selected = false;

              newData.push(infoItem);
            });

            component.infoItems.data = newData;
            component.infoVisible = true;
          });
      }
      self.selectedObservation = null;
    });

    this.map.on('areaselected', (evt) => {
      this.closeInfo();

      let bounds = (evt as any).bounds.toBBoxString().split(',')
      let newData: MapNetWorkComponentInfo[] = []
      component.mapService
        .getFeaturesWithMetadataInBoundingBox(bounds[1], bounds[0], bounds[3], bounds[2])
        .subscribe((res) => {
          res.features.forEach(f => {

            let infoItem = new MapNetWorkComponentInfo();
            infoItem.Id = f.properties['Id'];
            infoItem.ComponentType = f.properties['ComponentType'];
            let label = f.properties['ext_map_label_'] ?? f.properties['ext_identifica'] ?? '';
            infoItem.Label = label.split('\\r\\n').filter(str => str.length > 0).join(' ').split('\\n').filter(str => str.length > 0).join(' ')

            infoItem.Selected = false;

            newData.push(infoItem);
          });

          this.infoItems.data = newData;
          this.infoVisible = true;

          this.toggleAreaSelected(this);
        });
    });

    this.map.on('areaselecttoggled', function (e) {
      component.updateButton(component);
    });

    this.map.on('moveend', function (event) {
      component.mapBounds = event.target.getBounds();
      if (component.bboxFilterActive) {
        component.uiMapService.boundingBox$.next(component.mapBounds.toBBoxString());
      }
    });

    this.bboxSubscription = this.uiMapService
      .allowBoundingBoxFilter$
      .subscribe(o => this.checkBoundingBoxFilter(o));

    if (this.satnet) {
      this.refreshObservations();
    }

    this.mapInitialized = true;
  }

  public toggleSelected(item: MapNetWorkComponentInfo) {
    item.Selected = !item.Selected
  }

  public closeInfo() {
    this.infoItems.data = [];
    this.infoVisible = false;
    this.infoCoords = null;
  }

  public addComponentsToTask() {
    let toBeAdded = this.infoItems.data
      .filter(o => o.Selected)
      .map(o => ({
        TaskId: this.highlightItem.entityId,
        ComponentFeatureId: o.Id,
        ComponentType: this.taskNetworkComponentService.mapFeatureTypeToComponentType(o.ComponentType),
        Name: o.Label,
        Notes: ''
      } as TaskNetworkComponentConfig));

    this.taskNetworkComponentService.addComponentBatch(toBeAdded)
      .subscribe(() => {
        this.toaster.create(this.blahService.blah({
          key: 'task.networkComponents.numItemsAdded',
          params: [toBeAdded.length]
        }), {
          color: 'success'
        });

        this.closeInfo();
      });
  }

  public createTask(lat: number, lng: number, componentId: number = null, componentType: string = null, componentLabel: string = null) {

    let queryParams: object = {
      lat: lat,
      lng: lng,
      returnUrl: this.router.url
    }

    if (componentId != null && componentType != null && componentLabel != null) {
      queryParams = {
        ...queryParams,
        componentId: componentId,
        componentType: componentType,
        componentLabel: componentLabel,
      }
    }

    this.router.navigate([`Tasks/CreateNewTask`], { queryParams: queryParams });
    this.closeInfo();
  }

  public createTaskFromObservation() {
    if (this.selectedObservation) {
      this.creatingTaskFromObservation = true;
      this.mapService.createTaskFromObservation(this.selectedObservation.properties.Id)
        .pipe(finalize(() => {
          this.creatingTaskFromObservation = false;
        }))
        .subscribe({
          next: res => {
            this.selectedObservation = null;
            this.router.navigate([`Tasks/${res}/Details`]);
          }, error: () => {
            this.toaster.create(this.blahService.blah('default.saveFailed'));
          }
        });
    }
  }

  private getFeatureStyle(feature: any, layerName: string, zoom: number): L.PathOptions | L.CircleOptions | L.MarkerOptions {

    switch (feature.type) {
      case 1: // points

        // Rotating icons can be done by 
        // a) returning correct classname for icons. Position within tile must be taken into account when making transform rules.
        // b) using separate icons for rotated components
        // c) using custom featureToLayer function and overriding default image handling

        switch (feature.properties.component_type) {
          case 'pole':
            return {
              icon: L.icon({
                iconUrl: './assets/mapmarkers/component-icon-pole.png',
                iconSize: [30, 30],
                iconAnchor: [15, 15],
              })
            };

          case 'transformer':
            return {
              icon: L.icon({
                iconUrl: './assets/mapmarkers/component-icon-transformer.png',
                iconSize: [30, 30],
                iconAnchor: [15, 15],
              })
            }

          case 'switch':
            return {
              color: '#6f3c8a',
              weight: 2,
              radius: 10
            }

          case 'distribution_cabinet':
            return {
              icon: L.icon({
                iconUrl: './assets/mapmarkers/component-icon-distribution-cabinet.png',
                iconSize: [30, 30],
                iconAnchor: [15, 15],
              })
            }

          case 'distribution_transformer':
            return {
              color: '27215a',
              weight: 2,
              radius: 10
            }

          case 'disconnector_station':
            return {
              color: '#868686',
              weight: 2,
              radius: 10
            }

          case 'disconnector':
            return {
              icon: L.icon({
                iconUrl: './assets/mapmarkers/component-icon-disconnector.png',
                iconSize: [30, 30],
                iconAnchor: [15, 15],
              })
            }

          case 'subscription':
            return {
              icon: L.icon({
                iconUrl: './assets/mapmarkers/component-icon-subscription.png',
                iconSize: [30, 30],
                iconAnchor: [15, 15],
              })
            }

          default:
            return {
              color: '#000000',
              weight: 2,
              radius: 10
            }
        }

      case 2: // lines
      default: // and other

        switch (feature.properties.component_type) {
          case 'network':

            let color = feature.properties.color;
            switch (feature.properties.voltage_level) {
              case 'lv':
                color = 'blue';
                break;
              case 'mv':
                color = 'red'
                break;
              case 'hv':
                color = 'green';
                break;
              default:
                break;
            }


            return {
              dashArray: feature.properties.type == 'ground' ? '10, 10' : undefined,
              color: color,
              weight: 3
            }
          case 'route':
            return {
              color: 'black',
              weight: 1,
              opacity: .8
            }

          case 'satnet_risk':
            return {
              color: '#FD7F20',
              weight: 4,
            }

          default:
            return {
              color: '#666666',
              weight: 1
            }
        }
    }
  }

  private getSatnetFeatureColor(feature: GeoJSON.Feature): string {
    if (feature.properties.Metadata_snow_risk
      || feature.properties.Metadata_wedge
      || feature.properties.Metadata_wind_risk) {
      return '#ff0000';
    }

    return '#dc911b'
  }

  private filterFeature(feature: any, layerName: string, zoom: number): boolean {
    switch (feature.type) {
      case 1: // points
        return zoom >= 15;
      case 2: // lines
      default: // and other
        //return feature.properties.component_type != 'route';
        return true;
    }
  }

  private addSubLocationToLayer(feature: GeoJSON.Feature): void {
    if (feature.properties.systemGenerated) {
      this.lockedSubLocations.addData(feature);
    } else {
      this.subLocations.addData(feature);
    }
  }

  private getHighlightedMapItemDetails() {
    if (this.highlightItem === undefined) {
      return;
    }

    if (this.highlightItem.entityType == 'task') {

      forkJoin(
        [
          this.http.get(`${(this.ff_IwmPOC ? this.config.backendIwmPOCUri : this.config.backendIwmUri)}Api/ListTasks?$filter=Id eq ${this.highlightItem.entityId}&$select=ProgressStatus`),
          this.http.get(`${this.config.backendIwmUri}Api/Tasks?$filter=Id eq ${this.highlightItem.entityId}&$select=Id,TaskId,Identifier,Name`),
          this.http.get(`${this.config.backendIwmUri}Api/Locations?$filter=TargetId eq ${this.highlightItem.entityId}&$select=Latitude,Longitude`),
        ]
      ).pipe(
        take(1)
      ).subscribe(result => {
        let taskResult = result[1]['value'];
        let statusResult = result[0]['value'];
        let locationResult = result[2]['value'];

        if (taskResult && taskResult[0] && locationResult && locationResult[0]) {
          let task = taskResult[0];
          let status = statusResult.length > 0 ? statusResult[0] : {}
          let location = locationResult[0];
          if (
            location.Latitude == null
            || location.Longitude == null
          ) {
            return;
          }

          let color = 'white';
          switch (status.ProgressStatus) {
            case 'Archived':
              color = 'gray';
              break;
            case 'Finished':
              color = 'green';
              break;
            case 'Delayed':
              color = 'red';
              break;
            case 'Started':
              color = 'blue';
              break;
            case 'Open':
            default:
              color = 'white';
              break;
          }

          this.highlightedItem = <MapPinItem>{
            Id: task.Id.toLowerCase(),
            EntityType: 'task',
            Lat: location.Latitude,
            Lon: location.Longitude,
            Color: color,
            Title: `<div class="mat-typography"><p>${task.TaskId}</p><p>${task?.Identifier ?? ''} ${task?.Name ?? ''}</p></div>`,
            RouterLink: `Tasks/Open/${task.Id}`
          };

          this.updateMapMarkers();
          this.highlightMarker();
        }
      });
    } else if (this.highlightItem.entityType == 'worksite') {
      forkJoin(
        [
          this.http.get(`${this.config.backendIwmUri}WorksiteApi/Worksites?$filter=Id eq ${this.highlightItem.entityId}&$select=Id,IwmId,Identifier,Name,WorksiteStatus`),
          this.http.get(`${this.config.backendIwmUri}Api/Locations?$filter=TargetId eq ${this.highlightItem.entityId}&$select=Latitude,Longitude`),
        ]
      ).pipe(
        take(1)
      ).subscribe(result => {
        let worksiteResult = result[0]['value'];
        let locationResult = result[1]['value'];

        if (worksiteResult && worksiteResult[0] && locationResult && locationResult[0]) {
          let worksite = worksiteResult[0];
          let location = locationResult[0];
          if (
            location.Latitude == null
            || location.Longitude == null
          ) {
            return;
          }

          let color = 'white';
          switch (worksite.WorksiteStatuss) {
            case 'Archived':
              color = 'gray';
              break;
            case 'Completed':
              color = 'green';
              break;
            case 'Started':
              color = 'blue';
              break;
            case 'Created':
            default:
              color = 'white';
              break;
          }

          this.highlightedItem = <MapPinItem>{
            Id: worksite.Id.toLowerCase(),
            EntityType: 'worksite',
            Lat: location.Latitude,
            Lon: location.Longitude,
            Color: color,
            Title: `<div class="mat-typography"><p>${worksite.IwmId}</p><p>${worksite?.Identifier ?? ''} ${worksite?.Name ?? ''}</p></div>`,
            RouterLink: `Worksites/${worksite.Id}`
          };

          this.updateMapMarkers();
          this.highlightMarker();
        }
      });
    }

  }

  private refreshObservations() {
    // fetch new ones and add them to map
    this.mapService.getObservations().subscribe((res) => {
      res.forEach(observation => {
        this.observations.addData(observation);
      });
      let component = this;
      let deflate = L.deflate(
        {
          minSize: 20,
          markerLayer: this.markerClusterGroup,
          //markerType: L.circleMarker,
          markerOptions: function options(f) {

            let feat = (<GeoJSON.Feature>(<any>f).feature);
            let obsType = feat.properties.Type;

            let icon = 'satellite_alt';

            if (obsType == 'clear_cut' || obsType == 'thinning' || obsType == 'fallen_trees') {
              icon = 'forest';
            } else if (obsType == 'tall_tree' || obsType == 'dangerous_tree') {
              icon = 'height';
            } else if (obsType == 'snow_risk') {
              icon = 'ac_unit';
            } else if (obsType == 'wind_risk') {
              icon = 'air';
            } else if (obsType == 'construction_site') {
              icon = 'engineering';
            } else if (obsType == 'new_building' || obsType == 'demolished_building') {
              icon = 'house';
            } else if (obsType == 'new_road') {
              icon = 'add_road';
            }

            let color = component.getSatnetFeatureColor(feat);

            return {
              icon: L.divIcon({
                iconAnchor: [12, 12],
                html: `<div class="satnet-icon" style="border-color: ${color}"><span style="color: ${color}" class="material-symbols-outlined">${icon}</span><div>`
              })
            };

          }
        }
      );
      deflate.addTo(this.map);
      this.observations
        .bindTooltip(function (layer) {
          let text = '';

          let feat = <GeoJSON.Feature>(<any>layer).feature;

          text += component.blahService.blah('satnet.observationTypes.' + feat.properties.Type) + '<br/>';


          let time = dayjs.utc(feat.properties.ObservationReceived).toDate();
          let formatTime = dayjs(time).format('DD-MM-YYYY');

          text += formatTime + '<br/>';

          for (var prop in feat.properties) {
            if (prop.startsWith('Metadata_')) {
              let key = prop.substring(9);
              let translateValuePrefix = '';
              if (key == 'main_tree_species' || key == 'tree_species') {
                translateValuePrefix = 'satnet.metadata.treeSpecies.';
              }

              text += component.blahService.blah('satnet.metadata.keys.' + key)
                + ": "
                + (translateValuePrefix == ''
                  ? feat.properties[prop]
                  : component.blahService.blah(translateValuePrefix + feat.properties[prop]))
                + '<br/>';
            }
          }
          return text;
        })
        .on('click', (evt) => {
          this.showObservation(evt?.sourceTarget?.feature);
          // stop propagation
          L.DomEvent.stopPropagation(evt);
        })
        .addTo(deflate);
    });
  }

  private showObservation(observation) {
    if (!observation) {
      return;
    }
    this.selectedObservation = observation;
  }

  public close() {
    this.selectedObservation = null;
  }

  private highlightMarker() {
    if (this.highlightItem === undefined) {
      this.setEditControls(false);
      return;
    }
    const mapItem = this.getAllItems().find(o => o.Id === this.highlightItem.entityId.toLowerCase());
    if (mapItem === undefined || mapItem._marker === undefined) {
      return;
    }

    // Clear geoJson (previous sublocations)
    if (this.subLocations) {
      this.subLocations.clearLayers();
    }
    if (this.lockedSubLocations) {
      this.lockedSubLocations.clearLayers();
    }

    // fetch new ones and add them to map
    let subLocationMethod = undefined;
    if (this.highlightItem.entityType == 'task') {
      subLocationMethod = this.mapService.getTaskSubLocations(this.highlightItem.entityId);
    } else if (this.highlightItem.entityType == 'worksite') {
      subLocationMethod = this.mapService.getWorksiteSubLocations(this.highlightItem.entityId);
    }

    if (subLocationMethod) {
      subLocationMethod.subscribe((res) => {
        res.forEach(subLocation => {
          var feature = this.makeFeatureFromSubLocation(subLocation);
          this.addSubLocationToLayer(feature);
        });

        const geoJsonBounds = this.subLocations.getBounds();
        const lockedGeoJsonBounds = this.lockedSubLocations.getBounds();
        const markerBounds = mapItem._marker.getLatLng().toBounds(200);
        const bounds = geoJsonBounds.extend(lockedGeoJsonBounds).extend(markerBounds);
        this.setBounds(bounds);

        // enable edit controls
        this.setEditControls(true);
      });
    }
  }

  private setBounds(bounds: L.LatLngBounds) {
    if (bounds && bounds.isValid()) {
      if (this.smoothTransitions) {
        this.map.flyToBounds(bounds, { duration: 1, maxZoom: this.maxAutoZoom });
      } else {
        this.map.fitBounds(bounds, { maxZoom: this.maxAutoZoom });
      }
    }
  }

  private setEditControls(state: boolean): void {
    if (this.viewOnly) {
      state = false;
    }

    if (this.map.pm.controlsVisible() != state) {
      this.map.pm.toggleControls();
    }

    // select control
    if (state && this.metadataLayerAvailable) {
      if (this.selectControlButton === undefined) {

        this.selectControlButton = L.DomUtil.create('div',
          'leaflet-control leaflet-bar leaflet-select-control',
          (this.map as any)._controlCorners.topleft
        );
        this.selectControlButton.innerHTML = '<div title="Valitse komponentteja" class="icon" style="background-image: url(\'./assets/mapmarkers/control-area-select.png\')"></div>';

        let component = this;

        L.DomEvent.on(this.selectControlButton, 'click', function (e) {

          L.DomEvent.stopPropagation(e);
          component.toggleAreaSelected(component);
        });

        (this.map as any).selectArea.setControlKey(false);

        this.updateButton(this);
      }
    } else {
      (this.map as any).selectArea.disable();

      if (this.selectControlButton) {
        L.DomEvent.off(this.selectControlButton);
        L.DomUtil.remove(this.selectControlButton);
        this.selectControlButton = undefined;
      }


    }
  }

  private toggleAreaSelected(component) {
    if ((component.map as any).selectArea.enabled()) {
      //    L.DomUtil.removeClass(component.selectControlButton, 'active');
      (component.map as any).selectArea.disable();
    } else {
      //  L.DomUtil.addClass(component.selectControlButton, 'active');
      (component.map as any).selectArea.enable();
    }

    // disable info tool
    if (component.infoActive && (component.map as any).selectArea.enabled()) {
      component.toggleInfoActive(component);
    }
  }

  private toggleInfoActive(component) {
    component.infoActive = !component.infoActive;
    L.DomUtil[component.infoActive ? 'addClass' : 'removeClass'](component.infoControlButton, 'active');

    // disable area select
    if (component.infoActive && (component.map as any).selectArea.enabled()) {
      component.toggleAreaSelected(component);
    }

  }

  private updateButton(component) {
    let select = (component.map as any)?.selectArea;

    if (select && component.selectControlButton) {
      L.DomUtil[(component.map as any).selectArea.enabled() ? 'addClass' : 'removeClass'](component.selectControlButton, 'active');
    }
  }

  private updateMapMarkers(): void {
    if (!this.mapInitialized) {
      // not initialized yet
      return;
    }
    this.map.invalidateSize();

    // clear geoJson (previous sublocations)
    if (this.subLocations) {
      this.setEditControls(false);
      this.subLocations.clearLayers();
    }
    if (this.lockedSubLocations) {
      this.lockedSubLocations.clearLayers();
    }

    const thisComponent = this;
    const allItems = this.getAllItems();

    // Add new markers
    for (const c of allItems) {
      if (c._marker === undefined) {
        const marker = this.createMarker(c);
        if (this.useClusterMarkers) {
          this.markerClusterGroup.addLayer(marker);
        } else {
          marker.addTo(this.markerGroup);
        }

        if (c.RouterLink && !this.viewOnly) {
          marker.on('click', function (o) {
            thisComponent.highlightItem = { entityType: c.EntityType, entityId: c.Id };
            thisComponent.highlightMarker();
            thisComponent.router.navigate([c.RouterLink], {
              replaceUrl: true
            });
          });
        }
        c._marker = marker;
      }
    }

    // remove those that do not exist anymore
    if (this.useClusterMarkers) {
      this.markerClusterGroup.eachLayer(function (layer) {
        const exists = allItems.find(o => o._marker === layer);
        if (exists !== undefined) {
          return;
        }
        thisComponent.markerClusterGroup.removeLayer(layer);
      });
    } else {
      this.markerGroup.eachLayer(function (layer) {
        const exists = allItems.find(o => o._marker === layer);
        if (exists !== undefined) {
          return;
        }
        thisComponent.markerGroup.removeLayer(layer);
      });
    }



    if (this.bboxFilterActive) {
      return;
    }

    if (!this.highlightItem) { // do not override zoom to highlighted item
      if (allItems.length > 0) {

        if (this.useClusterMarkers) {
          this.setBounds(this.markerClusterGroup.getBounds().pad(0.1));
        } else {
          this.setBounds(this.markerGroup.getBounds().pad(0.1));
        }
      } else {
        this.map.flyTo(
          this.defaultPositionAndZoom.center,
          this.defaultPositionAndZoom.zoom
        );
      }
    }
  }

  private getAllItems(): MapPinItem[] {
    let allItems = [...this.items];
    if (this.highlightedItem !== null) {
      allItems.push(this.highlightedItem);
    }
    return allItems;
  }

  private createMarker(c: MapPinItem): L.Marker {
    const marker = L.marker(
      [c.Lat, c.Lon],
      <L.MarkerOptions>{
        itemId: c.Id,
        icon: this.mapService.getIcon(c.Color)
      }
    );
    if (c.Title) {
      marker.bindTooltip(c.Title);
    }

    return marker;
  }

  private makeFeatureFromSubLocation(subLocation: TaskSubLocation | WorksiteSubLocation): GeoJSON.Feature {

    if (subLocation instanceof TaskSubLocation) {
      return <GeoJSON.Feature>{
        "type": "Feature",
        "geometry": subLocation.Location,
        "properties": {
          "id": subLocation.Id,
          "taskId": subLocation.TaskId,
          "systemGenerated": subLocation.SystemGenerated,
        }
      };
    }

    return <GeoJSON.Feature>{
      "type": "Feature",
      "geometry": subLocation.Location,
      "properties": {
        "id": subLocation.Id,
        "worksiteId": subLocation.WorksiteId,
        "systemGenerated": false,
      }
    };

  }

  private toggleBoundingBoxFilter(component) {
    if (component.bboxFilterActive) {
      L.DomUtil.removeClass(component.bboxControlButton, 'active');
      component.bboxFilterActive = false;
      component.uiMapService.boundingBox$.next('');
    } else {
      L.DomUtil.addClass(component.bboxControlButton, 'active');
      component.bboxFilterActive = true;
      component.uiMapService.boundingBox$.next(component.mapBounds.toBBoxString());
    }
  }

  private checkBoundingBoxFilter(active: boolean) {

    // select control
    if (active) {
      if (this.bboxControlButton === undefined) {

        this.bboxControlButton = L.DomUtil.create('div',
          'leaflet-control leaflet-bar leaflet-select-control',
          (this.map as any)._controlCorners.topright
        );
        this.bboxControlButton.innerHTML = '<div title="Suodata listaa kartan alueelle" class="icon" style="background-image: url(\'./assets/mapmarkers/control-area-filter.png\')"></div>';

        let component = this;

        L.DomEvent.on(this.bboxControlButton, 'click', function (e) {

          L.DomEvent.stopPropagation(e);
          component.toggleBoundingBoxFilter(component);
        });
      }
    } else {
      if (this.bboxFilterActive) {
        this.toggleBoundingBoxFilter(this);
      }

      if (this.bboxControlButton) {
        L.DomEvent.off(this.bboxControlButton);
        L.DomUtil.remove(this.bboxControlButton);
        this.bboxControlButton = undefined;
      }
    }





  }


}

